/**
 * AlertsPage — Smart Alerts
 *
 * Single "what's wrong with my portfolio right now" view. Aggregates every
 * actionable issue from concentration, cash drag, property, mortgage,
 * tax allowances, IHT, FIRE and currency exposure into one ranked list.
 *
 * Source: GET /api/alerts/all
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Bell,
  RefreshCw,
  ShieldAlert,
  Banknote,
  Home,
  Landmark,
  Flame,
  Globe2,
  Scale,
  ArrowRight,
  CheckCircle2,
  X,
  Archive,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'
import RegimeBackdrop from '@/components/ui/RegimeBackdrop'

type Severity = 'CRITICAL' | 'HIGH' | 'MODERATE' | 'LOW'

export interface Alert {
  id: string
  category: string
  severity: Severity
  title: string
  message: string
  action: string
  action_url: string | null
  value_gbp: number
  ts: string
}

export interface AlertsResponse {
  alerts: Alert[]
  summary: {
    critical_count: number
    high_count: number
    moderate_count: number
    low_count: number
  }
  suppressed_count?: number
  household_total_gbp: number
  generated_at: string
}

/** Remove a dismissed alert from the response AND decrement its severity
 * count, so the pills don't advertise stale totals until a manual refresh.
 * Pure — exported so the contract stays pinned by tests. */
export function applyDismissal(data: AlertsResponse, alertId: string): AlertsResponse {
  const target = data.alerts.find(a => a.id === alertId)
  if (!target) return data
  const summary = { ...data.summary }
  const key = ({
    CRITICAL: 'critical_count',
    HIGH: 'high_count',
    MODERATE: 'moderate_count',
    LOW: 'low_count',
  } as const)[target.severity]
  if (key) summary[key] = Math.max(0, (summary[key] ?? 0) - 1)
  return {
    ...data,
    summary,
    alerts: data.alerts.filter(a => a.id !== alertId),
  }
}

const fmt = (n: number | null | undefined) =>
  (n == null || !Number.isFinite(n)) ? '£0' : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const SEVERITY_STYLES: Record<Severity, string> = {
  CRITICAL: 'text-rose-light bg-rose/10 border-rose/40',
  HIGH: 'text-amber-light bg-amber/10 border-amber/30',
  MODERATE: 'text-blue-light bg-blue/10 border-blue/30',
  LOW: 'text-text-secondary bg-slate-700/30 border-slate-600/40',
}

const SEVERITY_PILL: Record<Severity, string> = {
  CRITICAL: 'bg-rose/20 text-rose-light border border-rose/40',
  HIGH: 'bg-amber/20 text-amber-light border border-amber/30',
  MODERATE: 'bg-blue/20 text-blue-light border border-blue/30',
  LOW: 'bg-slate-700/40 text-text-secondary border border-slate-600/40',
}

const CATEGORY_META: Record<string, { label: string; icon: LucideIcon }> = {
  concentration: { label: 'Concentration', icon: ShieldAlert },
  cash: { label: 'Cash', icon: Banknote },
  property: { label: 'Property', icon: Home },
  tax: { label: 'Tax', icon: Landmark },
  iht: { label: 'IHT', icon: Scale },
  fire: { label: 'FIRE', icon: Flame },
  currency: { label: 'Currency', icon: Globe2 },
}

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'concentration', label: 'Concentration' },
  { key: 'cash', label: 'Cash' },
  { key: 'property', label: 'Property' },
  { key: 'tax', label: 'Tax' },
  { key: 'iht', label: 'IHT' },
  { key: 'fire', label: 'FIRE' },
  { key: 'currency', label: 'Currency' },
]

export default function AlertsPage() {
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('all')
  const [dismissing, setDismissing] = useState<Record<string, boolean>>({})
  const [clearing, setClearing] = useState(false)
  const [suppressedIds, setSuppressedIds] = useState<string[]>([])
  const [showSuppressed, setShowSuppressed] = useState(false)

  // Named `load`, not `fetch` — the old name shadowed window.fetch for the
  // whole component body.
  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [activeRes, suppressedRes] = await Promise.allSettled([
        api.get<AlertsResponse>('/api/alerts/all'),
        api.get<{ suppressed_ids: string[]; count: number }>('/api/alerts/suppressed'),
      ])
      if (activeRes.status === 'fulfilled') setData(activeRes.value.data)
      if (suppressedRes.status === 'fulfilled') setSuppressedIds(suppressedRes.value.data?.suppressed_ids ?? [])
      if (activeRes.status === 'rejected') setError((activeRes.reason as any)?.message ?? 'Failed to load alerts')
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }

  const restoreOne = async (alertId: string) => {
    try {
      await api.post(`/api/alerts/unsuppress/${encodeURIComponent(alertId)}`)
      setSuppressedIds(prev => prev.filter(id => id !== alertId))
      // Reload active alerts so the restored one reappears
      load()
    } catch (err: any) {
      setError(err?.message ?? 'Restore failed')
    }
  }

  const dismissOne = async (alertId: string) => {
    setDismissing(prev => ({ ...prev, [alertId]: true }))
    try {
      await api.post(`/api/alerts/suppress/${encodeURIComponent(alertId)}?days=30`)
      // Remove optimistically AND keep the severity pills + dismissed list in
      // step — previously both stayed stale until a manual refresh.
      setData(prev => prev ? applyDismissal(prev, alertId) : prev)
      setSuppressedIds(prev => (prev.includes(alertId) ? prev : [...prev, alertId]))
    } catch (err: any) {
      setError(err?.message ?? 'Dismiss failed')
    } finally {
      setDismissing(prev => {
        const next = { ...prev }; delete next[alertId]; return next
      })
    }
  }

  const clearAll = async () => {
    if (!confirm('Dismiss every currently-active alert for 30 days? They\'ll reappear after that window or can be restored individually.')) return
    setClearing(true)
    try {
      await api.post('/api/alerts/clear-all?days=30')
      await load()
    } catch (err: any) {
      setError(err?.message ?? 'Clear all failed')
    } finally {
      setClearing(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo<Alert[]>(() => {
    if (!data) return []
    if (filter === 'all') return data.alerts
    return data.alerts.filter(a => a.category === filter)
  }, [data, filter])

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-24" />
        <div className="skeleton h-40" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={load} className="btn-secondary mt-3">
          Retry
        </button>
      </div>
    )
  }

  const { summary, alerts } = data
  const totalAlerts = alerts.length

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Smart Alerts"
        icon={Bell}
        iconClassName="text-amber-light"
        subtitle={
          <>
            Every actionable issue across your household, ranked by urgency
            {suppressedIds.length > 0 && (
              <>
                {' '}·{' '}
                <button
                  type="button"
                  onClick={() => setShowSuppressed(s => !s)}
                  className="underline text-text-secondary hover:text-text-primary"
                >
                  {suppressedIds.length} dismissed {showSuppressed ? '(hide)' : '(show)'}
                </button>
              </>
            )}
          </>
        }
        actions={
          <>
            {alerts.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                disabled={clearing}
                className="btn-secondary gap-2 text-xs"
                title="Dismiss all currently-active alerts for 30 days"
              >
                <Archive size={13} aria-hidden />
                {clearing ? 'Clearing…' : `Clear all (${alerts.length})`}
              </button>
            )}
            <button type="button" onClick={load} className="btn-secondary gap-2 text-xs">
              <RefreshCw size={13} aria-hidden />
              Refresh
            </button>
          </>
        }
      />

      {/* Live regime backdrop — alerts fire with more urgency in risk-off */}
      <RegimeBackdrop variant="risk" />

      {/* Dismissed alerts panel (collapsible) */}
      {showSuppressed && suppressedIds.length > 0 && (
        <div className="card p-4 border-dashed border-amber-500/30 bg-amber-500/[0.03]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Archive size={14} className="text-amber-400" />
              Dismissed alerts ({suppressedIds.length})
            </h2>
            <p className="text-[11px] text-text-muted">
              Suppressed for 30 days. Click restore to re-enable an alert.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {suppressedIds.map(id => (
              <div key={id}
                className="flex items-center gap-2 px-2.5 py-1 rounded border border-border bg-surface-card text-xs font-mono text-text-muted">
                <span className="truncate max-w-[280px]" title={id}>{id}</span>
                <button
                  type="button"
                  onClick={() => restoreOne(id)}
                  className="text-blue-400 hover:text-blue-300 text-[11px]"
                  title="Restore this alert now"
                >
                  restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Severity counter pills */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <div className={clsx('stat-card border', SEVERITY_STYLES.CRITICAL)}>
          <p className="stat-label">Critical</p>
          <p className="stat-value">{summary.critical_count}</p>
          <p className="text-xs text-text-muted">act immediately</p>
        </div>
        <div className={clsx('stat-card border', SEVERITY_STYLES.HIGH)}>
          <p className="stat-label">High</p>
          <p className="stat-value">{summary.high_count}</p>
          <p className="text-xs text-text-muted">act this week</p>
        </div>
        <div className={clsx('stat-card border', SEVERITY_STYLES.MODERATE)}>
          <p className="stat-label">Moderate</p>
          <p className="stat-value">{summary.moderate_count}</p>
          <p className="text-xs text-text-muted">act this month</p>
        </div>
        <div className={clsx('stat-card border', SEVERITY_STYLES.LOW)}>
          <p className="stat-label">Low</p>
          <p className="stat-value">{summary.low_count}</p>
          <p className="text-xs text-text-muted">keep an eye on</p>
        </div>
      </div>

      {/* Filter buttons */}
      <div className="glass rounded-xl p-2 flex flex-wrap gap-2">
        {FILTERS.map(f => {
          const count =
            f.key === 'all'
              ? alerts.length
              : alerts.filter(a => a.category === f.key).length
          const active = filter === f.key
          return (
            <button
              type="button"
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                active
                  ? 'bg-blue/20 text-blue-light border-blue/40'
                  : 'bg-surface-card text-text-secondary border-border hover:bg-surface-hover',
              )}
            >
              {f.label}
              <span className="ml-1.5 text-[10px] text-text-muted">{count}</span>
            </button>
          )
        })}
      </div>

      {/* Alert list */}
      <section>
        {filtered.length === 0 ? (
          <div className="card p-10 text-center">
            <CheckCircle2
              size={32}
              className="text-emerald mx-auto mb-2"
              aria-hidden
            />
            <p className="text-lg font-semibold text-text-primary">
              {totalAlerts === 0 ? 'All clear ✓' : 'No alerts in this category'}
            </p>
            <p className="text-xs text-text-muted mt-1">
              {totalAlerts === 0
                ? 'Your portfolio has no outstanding risks, allowances or urgent items.'
                : 'Try switching to a different category filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(alert => {
              const meta = CATEGORY_META[alert.category] || {
                label: alert.category,
                icon: AlertTriangle,
              }
              const Icon = meta.icon
              return (
                <div
                  key={alert.id}
                  className={clsx(
                    'card p-4 border-l-4',
                    alert.severity === 'CRITICAL' && 'border-l-rose',
                    alert.severity === 'HIGH' && 'border-l-amber',
                    alert.severity === 'MODERATE' && 'border-l-blue',
                    alert.severity === 'LOW' && 'border-l-slate-500',
                  )}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span
                          className={clsx(
                            'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                            SEVERITY_PILL[alert.severity],
                          )}
                        >
                          {alert.severity}
                        </span>
                        <span className="badge-neutral !text-[10px] flex items-center gap-1">
                          <Icon size={10} aria-hidden />
                          {meta.label}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-text-primary">
                        {alert.title}
                      </p>
                      <p className="text-xs text-text-secondary mt-1">
                        {alert.message}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      {alert.value_gbp > 0 && (
                        <>
                          <p
                            className={clsx(
                              'text-lg font-bold font-mono',
                              alert.severity === 'CRITICAL' && 'text-rose-light',
                              alert.severity === 'HIGH' && 'text-amber-light',
                              alert.severity === 'MODERATE' && 'text-blue-light',
                              alert.severity === 'LOW' && 'text-text-secondary',
                            )}
                          >
                            {fmt(alert.value_gbp)}
                          </p>
                          <p className="text-[10px] text-text-muted">impact</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-text-muted italic">{alert.action}</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => dismissOne(alert.id)}
                        disabled={!!dismissing[alert.id]}
                        className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary px-2 py-1.5 rounded border border-border hover:border-border-light transition disabled:opacity-50"
                        title="Dismiss for 30 days"
                      >
                        <X size={12} aria-hidden />
                        {dismissing[alert.id] ? 'Dismissing…' : 'Dismiss'}
                      </button>
                      {alert.action_url && (
                        <Link
                          to={alert.action_url}
                          className="btn-secondary gap-1 text-xs py-1.5 px-3"
                        >
                          Take action
                          <ArrowRight size={12} aria-hidden />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Footer — methodology */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          How Smart Alerts are generated
        </summary>
        <div className="mt-3 space-y-1.5">
          <p>Household total tracked: £{(data.household_total_gbp ?? 0).toLocaleString('en-GB')}.</p>
          <p>
            Alerts are aggregated from: concentration risk (single-name &gt; 5% of household
            or &gt; 25% of an account), cash drag (touchable cash with &gt; £100/yr foregone
            interest), property urgent items, mortgage remortgage warnings (fixed-rate ending in
            &lt; 12 months), tax allowance underuse, IHT exposure (current + April 2027 cliff),
            FIRE drift (below Standard FIRE 28.5×) and currency exposure (non-GBP &gt; 30%).
          </p>
          <p>
            Ranked by severity (CRITICAL &gt; HIGH &gt; MODERATE &gt; LOW) then by £ impact
            descending. Each upstream module is isolated — a failure in one check never
            stops the others.
          </p>
        </div>
      </details>
    </div>
  )
}
